import { selectCurrentHotelId } from 'accessors'
import DragAndDropUploader from 'components/ReusableComponents/DragAndDropUploader'
import CustomHeader from 'components/ReusableComponents/HeaderTitles/index'
import { useEngagePlusGate } from 'components/ReusableComponents/UnlockServiceDialog'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Chat from 'components/ReusableComponents/Messaging/Chat/index'
import ChatLockedBar from 'components/ReusableComponents/Messaging/ChatLockedBar/index'
import ChatOwnerBar from 'components/ReusableComponents/Messaging/ChatOwnerBar/index'
import Compose from 'components/ReusableComponents/Messaging/Compose/index'
import ComposeEmail from 'components/ReusableComponents/Messaging/ComposeEmail'
import Bubble from 'components/ReusableComponents/Messaging/ConversationBubble/index'
import EnabledNotificationsBox from 'components/ReusableComponents/Messaging/EnableNotificationBox'
import EnableNotificationPrefenceBox from 'components/ReusableComponents/Messaging/EnableNotificationPreferenceBox'
import IntentSelector from 'components/ReusableComponents/Messaging/IntentSelector/index'
import NoMessages from 'components/ReusableComponents/Messaging/NoMessages/index'
import Sidebar from 'components/ReusableComponents/Messaging/Sidebar/index'
import TypingMessage from 'components/ReusableComponents/Messaging/TypingMessage/index'
import UserInfo from 'components/ReusableComponents/Messaging/UserInfo/index'
import { isSameDay } from 'date-fns'
import { acceptedFileTypes, maxFileSize } from 'features/Messaging/fileUploadConfig'
import FollowUpButtons from 'features/Messaging/FollowUpButtons'
import GuestInfoSidebar from 'features/Messaging/GuestInfoSidebar'
import { useInternalAdmin } from 'hooks'
import useInterval from 'hooks/useInterval'
import useLocalStorage from 'hooks/useLocalStorageState'
import usePlausible from 'hooks/usePlausible'
import { memo, useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import {
  getChats,
  getThread,
  sendMessage,
  updatePendingThreadCount,
  updateTypingUsers
} from '../../actions/messaging'
import Arrivals from '../../features/Arrivals/index'
import DateDivider from './DateDivider/index'
import FileUploadDialog from './FileUploadDialog'
import { getStoreNameFromMessagingType } from './messaging-helpers'
import styles from './Messaging.module.scss'

const typingUsersCooldown = 480000

function cleanedTypingUsers(typingUsers) {
  const now = Date.now()

  const filtered = Object.values(typingUsers).filter(
    u => now - u.startedOn < typingUsersCooldown
  )

  return filtered
}

function isStarter(m, i, arr) {
  const p = arr[i - 1]
  if (
    !p ||
    m.isSenderClient !== p.isSenderClient ||
    m.senderName !== p.senderName ||
    !isSameDay(new Date(m.scheduledOn), new Date(p.scheduledOn))
  ) {
    return true
  }

  return false
}

const IntervalWrapper = memo(({ messagingType }) => {
  const store = getStoreNameFromMessagingType(messagingType)

  const typingUsers = useSelector(s => s[store].typingUsers, shallowEqual)
  const dispatch = useDispatch()

  useInterval(() => {
    dispatch(updateTypingUsers(cleanedTypingUsers(typingUsers), messagingType))
  }, typingUsersCooldown / 2)

  return null
})

const Messaging = ({ messagingType, title }) => {
  const dispatch = useDispatch()
  const { promptEngagePlusUpgradeOnEntry } = useEngagePlusGate()

  const { postEvent, plausibleEvents } = usePlausible()

  const [showHint, setShowHint] = useState(false)

  const [notificationsPermission, setNotificationPermission] = useState(
    window.Notification && Notification.permission
  )

  const { chatId: currentchatId } = useParams()

  useEffect(() => {
    document.querySelector('body').classList.add('darkBody')

    return () => {
      document.querySelector('body').classList.remove('darkBody')
    }
  }, [])

  useEffect(() => {
    if (messagingType === 'service') {
      promptEngagePlusUpgradeOnEntry()
    }
  }, [messagingType, promptEngagePlusUpgradeOnEntry])

  const isInternalAdmin = useInternalAdmin()

  const { chatId } = useParams()

  const store = getStoreNameFromMessagingType(messagingType)

  const threads = useSelector(s => s[store].threads)
  const loading = useSelector(s => s[store].loading)
  const threadLoading = useSelector(s => s[store].threadLoading)
  const typingUsers = useSelector(s => s[store].typingUsers, shallowEqual)
  const currentUserName = useSelector(s => s.login.currentUser.name)
  const currentSubsidiaryId = useSelector(selectCurrentHotelId)

  const [mediumToSend, setMediumToSend] = useState('sms')

  const notificationPreferences = useSelector(
    s => s.notifications.notificationPreferences
  )

  const [currentThread, setCurrentThread] = useState()

  const [isNotificationRequestRejected, setIsNotificationRequestRejected] =
    useLocalStorage('isNotificationRequestRejected', false)

  function refreshChat() {
    dispatch(getChats(messagingType))
    dispatch(getThread(chatId, messagingType))
  }

  useEffect(() => {
    dispatch(getChats(messagingType))
    dispatch(updatePendingThreadCount())
  }, [dispatch, messagingType])

  useEffect(() => {
    if (chatId) {
      dispatch(getThread(chatId, messagingType))
    }
  }, [chatId, dispatch, messagingType])

  useEffect(() => {
    setCurrentThread(
      threads[Object.keys(threads).find(k => Number(k) === Number(currentchatId))]
    )
  }, [threads, currentchatId])

  function showHintIfShould() {
    if (window.innerWidth < 1301 && !localStorage.getItem('seenSidebarHint')) {
      setShowHint(true)
    } else {
      setShowHint(false)
    }
  }

  useEffect(() => {
    if (!localStorage.getItem('seenSidebarHint')) {
      window.addEventListener('resize', showHintIfShould)
      showHintIfShould()
    }

    return () => window.removeEventListener('resize', showHintIfShould)
  })

  // function walkmeCallback(state) {
  //   if (state.action === 'reset') {
  //     localStorage.setItem('seenSidebarHint', true)
  //   }
  // }

  const [intentVisibleForChatId, setIntentVisibleForChatId] = useState()
  const [intentSelectorPos, setIntentSelectorPos] = useState({ x: 0, y: 0 })

  const [intents, setIntents] = useState({})

  function handleClickIntentButton(chatMessageId, e) {
    setIntentVisibleForChatId(chatMessageId)

    const { left: x, top: y } = e.target.getBoundingClientRect()

    setIntentSelectorPos({ x, y })
  }

  function handleSelectIntent(intentType) {
    setIntents({
      ...intents,
      [intentVisibleForChatId]: intentType
    })
    setIntentVisibleForChatId(null)
  }

  const [isUploadDialogVisible, setIsUploadDialogVisible] = useState(false)
  const [filesToSend, setFilesToSend] = useState([])

  const [dragCount, setDragCount] = useState(0)
  const [isDragging, setIsDragging] = useState(false)

  function handleDragOver(e) {
    e.preventDefault()
    e.stopPropagation()
    setDragCount(n => n + 1)
    setIsDragging(true)
  }

  function handleDrop() {
    setDragCount(0)
    setIsDragging(false)
  }

  function handleDragLeave(e) {
    e.preventDefault()
    e.stopPropagation()
    setDragCount(n => n - 1)
  }

  useEffect(() => {
    if (dragCount === 0) {
      setIsDragging(false)
    }
  }, [dragCount])

  const [showFilePond, setShowFilePond] = useState(false)

  function handleUploadedFiles(file) {
    setIsUploadDialogVisible(false)
    setDragCount(0)
    setShowFilePond(false)
    setIsDragging(false)
    setFilesToSend(prevFiles => [
      ...prevFiles,
      {
        path: file.fileName,
        url: file.temporaryUrl.tempPublicPath,
        tags: file.tags
      }
    ])
  }

  function handleUpdateFiles(files) {
    if (files.length === 0) {
      setDragCount(0)
      setIsDragging(false)
      setShowFilePond(false)
    } else {
      setShowFilePond(true)
      setDragCount(0)
      setIsDragging(false)
    }
  }

  function sendSms(message) {
    postEvent(plausibleEvents.SendNewChatMessage, { messagingType })

    dispatch(
      sendMessage({
        message,
        chatId: currentchatId,
        senderName: currentUserName,
        messagingType,
        mediaUrls: filesToSend.map(f => f.path),
        messageKind: 'Sms'
      })
    )
  }

  function sendEmail(message, subject, email) {
    postEvent(plausibleEvents.SendNewEmail, { messagingType })

    dispatch(
      sendMessage({
        message,
        subject,
        messageKind: 'Email',
        chatId: currentchatId,
        senderName: currentUserName,
        messagingType,
        email
      })
    )
  }

  if (
    !isNotificationRequestRejected &&
    Notification &&
    notificationsPermission !== 'granted'
  ) {
    return (
      <EnabledNotificationsBox
        permission={notificationsPermission}
        setNotificationPermission={setNotificationPermission}
        isNotificationRequestRejected={isNotificationRequestRejected}
        setIsNotificationRequestRejected={setIsNotificationRequestRejected}
      />
    )
  }

  if (loading) {
    return <LoadingIndicator />
  }

  if (
    !isInternalAdmin &&
    notificationPreferences &&
    !notificationPreferences
      .find(
        n =>
          n.kind ===
          (messagingType === 'review' ? 'NegativeReview' : 'WaitingClientChatExists')
      )
      .subsidiaries.includes(currentSubsidiaryId)
  ) {
    return <EnableNotificationPrefenceBox messagingType={messagingType} />
  }

  return (
    <>
      {isUploadDialogVisible && (
        <FileUploadDialog
          handleClose={() => setIsUploadDialogVisible(false)}
          handleUploadedFiles={handleUploadedFiles}
          maxFileSize={maxFileSize}
          acceptedFileTypes={acceptedFileTypes}
        />
      )}
      <IntervalWrapper messagingType={messagingType} />
      <CustomHeader title={title} width={1300}>
        {messagingType === 'service' && (
          <div className={styles.customHeader}>
            <Arrivals noList />
          </div>
        )}
        {isInternalAdmin && <FollowUpButtons />}
      </CustomHeader>

      <div className={styles.wrapper}>
        <Sidebar messagingType={messagingType} currentThread={currentThread} />
        {intentVisibleForChatId && (
          <div
            className={styles.overlay}
            onClick={() => setIntentVisibleForChatId(null)}
          ></div>
        )}
        {intentVisibleForChatId && (
          <IntentSelector
            {...intentSelectorPos}
            handleClose={() => setIntentVisibleForChatId(null)}
            handleSelect={handleSelectIntent}
          />
        )}
        <div
          className={styles.chatContainer}
          onDragEnter={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {isDragging && (
            <div className={styles.dragOverlay}>
              <div className={styles.dropZone}>
                <div className={styles.dropZoneInner}>
                  <i className="filepond-upload-icon"></i>
                  <p className="filepond-p" style={{ marginTop: 10 }}>
                    drop an file here to upload
                  </p>
                </div>
              </div>
            </div>
          )}
          {currentThread && currentThread.messages && !threadLoading && (
            <>
              <UserInfo
                {...currentThread.bookingInfo}
                messagingType={messagingType}
                restrictedDateName={currentThread.restrictedName}
                name={currentThread.subscriber}
                roomNo={currentThread.roomNumber}
                chatId={currentchatId}
                resolvable={currentThread && currentThread.status !== 'Closed'}
                loyaltyLevel={currentThread.loyaltyLevel}
                loyaltyProgram={currentThread.loyaltyProgram}
                subscriptionStatus={currentThread.subscriptionStatus}
                isResolving={currentThread.isResolving}
                hotelId={currentSubsidiaryId}
              />
              {(currentThread.subscriptionStatus === 'Active' ||
                currentThread.subscriptionStatus === 'OptedIn') && (
                <ChatOwnerBar
                  ownedBy={currentThread.ownedBy}
                  chatId={currentchatId}
                  messagingType={messagingType}
                />
              )}
              {currentThread.subscriptionStatus === 'DoNotCall' && (
                <ChatLockedBar color="#FF003D">
                  Guest is opted out. You can still send them non-marketing messages.
                </ChatLockedBar>
              )}
              <Chat>
                {currentThread.messages.reduce((pm, m, i, arr) => {
                  return [
                    ...pm,
                    <DateDivider
                      key={'d' + (m.chatMessageId || m.controlKey)}
                      prevMessage={arr[i - 1] || null}
                      currentMessage={m}
                    />,
                    <Bubble
                      {...m}
                      starter={isStarter(m, i, arr)}
                      key={m.chatMessageId || m.controlKey}
                      messagingType={messagingType}
                      isLastMessage={i + 1 === arr.length}
                      highlighted={m.chatMessageId === intentVisibleForChatId}
                      handleClickIntentButton={e =>
                        handleClickIntentButton(m.chatMessageId, e)
                      }
                      intent={intents[m.chatMessageId] || null}
                      messageId={m.chatMessageId}
                    >
                      {m.content}
                    </Bubble>
                  ]
                }, [])}
              </Chat>
            </>
          )}
          {currentThread && threadLoading && <LoadingIndicator />}
          {!currentThread && <NoMessages />}
          <TypingMessage typingUsers={typingUsers} />
          <div
            style={{
              display: showFilePond ? 'block' : 'none'
            }}
            className={styles.filepondHolder}
          >
            {currentchatId && !isUploadDialogVisible && (
              <DragAndDropUploader
                url="/media"
                dropOnPage={!isUploadDialogVisible}
                dropOnElement={false}
                acceptedFileTypes={acceptedFileTypes}
                maxFileSize={maxFileSize}
                onData={formData => formData}
                onLoad={res => {
                  const data = JSON.parse(res)
                  handleUploadedFiles(data.result)
                }}
                onUpdateFiles={handleUpdateFiles}
                onError={res => console.warn('file response', res)}
                labelIdle=""
                labelFileProcessingError={err => `${err.code} - Something went wrong`}
              />
            )}
          </div>
          {mediumToSend === 'email' && (
            <ComposeEmail
              chatId={currentchatId}
              closed={currentThread && currentThread.status === 'Closed'}
              currentEmail={currentThread && currentThread.subscriberEmail}
              disabled={!currentchatId}
              handleClickAddFile={() => setIsUploadDialogVisible(true)}
              filesToSend={filesToSend}
              setFilesToSend={setFilesToSend}
              subscriberUtcOffSet={currentThread?.subscriberUtcOffSet}
              canShowImagesHolder={!showFilePond}
              setMedium={setMediumToSend}
              handleSubmit={sendEmail}
            />
          )}
          {mediumToSend === 'sms' && (
            <Compose
              chatId={currentchatId}
              closed={currentThread && currentThread.status === 'Closed'}
              disabled={!currentchatId}
              handleClickAddFile={() => setIsUploadDialogVisible(true)}
              filesToSend={filesToSend}
              setFilesToSend={setFilesToSend}
              subscriberUtcOffSet={currentThread?.subscriberUtcOffSet}
              canShowImagesHolder={!showFilePond}
              setMedium={setMediumToSend}
              messagingType={messagingType}
              handleSubmit={sendSms}
              guestName={currentThread?.subscriber}
            />
          )}
        </div>
        {currentThread && currentThread.messages && !threadLoading && (
          <GuestInfoSidebar
            {...currentThread.bookingInfo}
            subscriberId={currentThread.subscriberId}
            roomNo={currentThread.roomNumber}
            subscriberUtcOffSet={currentThread.subscriberUtcOffSet}
            subsidiaryPhone={currentThread.subsidiaryPhone}
            chatId={currentchatId}
            refreshChat={refreshChat}
            messagingType={messagingType}
          />
        )}
      </div>
    </>
  )
}

// const joyrideSteps = [
//   {
//     content: (
//       <div className="sidebarTooltip2">Click on menu icon to expand the menu.</div>
//     ),
//     target: '.no-sidebar-hamburger',
//     disableBeacon: false
//   }
// ]

export default Messaging
